import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  MANIFEST_COMPANY,
  MANIFEST_LABEL,
  agentBlockFromDraft,
  manifestSafeFileStem,
  splitManifestAddressLines,
} from './shipmentCargoManifest';

const TABLE_HEADERS = [
  'NO',
  'HBL',
  'SHIPPER DETAILS',
  'CONSIGNEE DETAILS',
  'PCS',
  'CBM',
  'WEIGHT',
  'DESCRIPTION',
  'UPB/GIFT',
];

/** Split "EMAIL : x" / "TEL : x" for styled PDF / UI. */
export function parseManifestLabeledLine(line) {
  const s = String(line || '');
  let m = s.match(/^(EMAIL\s*:\s*)(.*)$/i);
  if (m) return { label: m[1], value: m[2].trim() };
  m = s.match(/^(TEL\s*:\s*)(.*)$/i);
  if (m) return { label: m[1], value: m[2].trim() };
  return { label: '', value: s };
}

/**
 * Draw `label` (Helvetica normal) + `value` (bold); value may wrap. Returns line count (3.5mm steps).
 */
function pdfDrawLabelBoldValue(doc, x, y, label, value, maxTotalWidth) {
  const lab = String(label);
  const val = String(value ?? '—');
  const fs = doc.getFontSize() || 8.5;
  doc.setFontSize(fs);
  doc.setFont('helvetica', 'normal');
  doc.text(lab, x, y);
  const afterLabelX = x + doc.getTextWidth(lab);
  const maxValW = Math.max(16, maxTotalWidth - doc.getTextWidth(lab));
  doc.setFont('helvetica', 'bold');
  const valLines = doc.splitTextToSize(val, maxValW);
  const lines = valLines.length ? valLines : [''];
  lines.forEach((ln, i) => {
    doc.text(ln, i === 0 ? afterLabelX : x, y + i * 3.5);
  });
  doc.setFont('helvetica', 'normal');
  return lines.length;
}

/** Column A blank; B–J = 9 manifest table columns (matches typical Excel layout). */
const EXCEL_COLS = 10;
const padRow = (cells) => {
  const row = Array.isArray(cells) ? [...cells] : [];
  while (row.length < EXCEL_COLS) row.push('');
  return row.slice(0, EXCEL_COLS);
};

function letterheadRowMergeExcel(merges, r) {
  merges.push({ s: { r, c: 1 }, e: { r, c: 4 } }, { s: { r, c: 5 }, e: { r, c: 9 } });
}

function buildSheetAoA(meta, agent, rows) {
  const leftAddr = splitManifestAddressLines(MANIFEST_COMPANY.address);
  const rightAddr = splitManifestAddressLines(agent.address || '');
  const addrRows = Math.max(leftAddr.length, rightAddr.length, 1);

  const shipperEmailLine = MANIFEST_COMPANY.email
    ? `${MANIFEST_LABEL.email} ${MANIFEST_COMPANY.email}`
    : '';
  const shipperTelLine = `${MANIFEST_LABEL.tel} ${MANIFEST_COMPANY.tel}`;
  const agentEmailLine = agent.emailLine || '';
  const agentTelLine = agent.telLine || '';

  const aoa = [];
  const titleRow = Array(EXCEL_COLS).fill('');
  titleRow[0] = 'CARGO MANIFEST';
  aoa.push(titleRow);

  aoa.push(padRow(['', MANIFEST_COMPANY.name, '', '', '', agent.name || '']));

  for (let i = 0; i < addrRows; i += 1) {
    aoa.push(padRow(['', leftAddr[i] || '', '', '', '', rightAddr[i] || '', '', '', '', '']));
  }

  aoa.push(padRow(['', shipperEmailLine, '', '', '', agentEmailLine, '', '', '', '']));
  aoa.push(padRow(['', shipperTelLine, '', '', '', agentTelLine, '', '', '', '']));

  /** Summary block: label | value (cols B–C), gap col D, label col E, gap F–G, value col H (ref. sheet). */
  aoa.push(
    padRow([
      '',
      'BILL OF LADING NO:',
      meta.billOfLadingNo || '—',
      '',
      'TOTAL PACKAGES:',
      '',
      '',
      String(meta.totalPackages),
      '',
      '',
    ]),
  );
  aoa.push(
    padRow([
      '',
      'VESSEL:',
      meta.vessel || '—',
      '',
      'TOTAL CBM:',
      '',
      '',
      String(meta.totalCbm),
      '',
      '',
    ]),
  );
  aoa.push(
    padRow([
      '',
      'CONTAINER NO:',
      meta.container || '—',
      '',
      'TOTAL WEIGHT:',
      '',
      '',
      `${meta.totalWeightKg} KG`,
      '',
      '',
    ]),
  );

  const blank = padRow([]);
  const head = padRow(['', ...TABLE_HEADERS]);
  const body = rows.map((r) =>
    padRow([
      '',
      r.no,
      r.hbl,
      r.shipperDetails,
      r.consigneeDetails,
      String(r.pcs),
      (Math.round(r.cbm * 100) / 100).toFixed(2),
      String(Math.round(r.weight) || r.weight),
      r.description,
      r.upbGift,
    ]),
  );

  aoa.push(blank, blank, head, ...body);

  const merges = [];
  merges.push({ s: { r: 0, c: 0 }, e: { r: 0, c: 9 } });
  letterheadRowMergeExcel(merges, 1);
  for (let i = 0; i < addrRows; i += 1) {
    letterheadRowMergeExcel(merges, 2 + i);
  }
  const contactEmailR = 2 + addrRows;
  const contactTelR = 3 + addrRows;
  letterheadRowMergeExcel(merges, contactEmailR);
  letterheadRowMergeExcel(merges, contactTelR);

  return { aoa, merges };
}

/**
 * @param {{ rows: object[], draft: object, meta: object, shipmentCode?: string, trackingNumber?: string }} opts
 */
export function downloadCargoManifestExcel(opts) {
  const { rows, draft, meta, shipmentCode, trackingNumber } = opts;
  const agent = agentBlockFromDraft(draft);
  const { aoa, merges } = buildSheetAoA(meta, agent, rows);
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!merges'] = merges;
  /* A = spacer; B/C left summary; D gap; E label totals; F–G gap; H = values (matches reference sheet). */
  ws['!cols'] = [
    { wch: 2 },
    { wch: 22 },
    { wch: 28 },
    { wch: 3 },
    { wch: 18 },
    { wch: 2 },
    { wch: 2 },
    { wch: 14 },
    { wch: 36 },
    { wch: 12 },
  ];

  const headerRowIndex = aoa.findIndex((row) => row[1] === 'NO' && row[2] === 'HBL');
  if (headerRowIndex >= 0) {
    ws['!rows'] = [];
    for (let r = headerRowIndex + 1; r < aoa.length; r += 1) {
      ws['!rows'][r] = { hpt: 96 };
    }
  }

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Cargo manifest');
  const stem = manifestSafeFileStem(shipmentCode, trackingNumber);
  XLSX.writeFile(wb, `${stem}_cargo-manifest.xlsx`);
}

/**
 * A4 landscape, multi-page.
 */
export function downloadCargoManifestPdf(opts) {
  const { rows, draft, meta, shipmentCode, trackingNumber } = opts;
  const agent = agentBlockFromDraft(draft);
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 6;
  const colMid = pageW / 2;
  /** Fixed-width right “column” anchored to page margin (matches on-screen rail + mockup). */
  const rightColTargetWidthMm = 118;
  const rightColStart = Math.max(colMid + 8, pageW - margin - rightColTargetWidthMm);
  const rightTextWidth = Math.max(48, pageW - margin - rightColStart - 2);
  let y = 12;

  /** Light panel behind letterhead + summary (painted first so text sits on top). */
  const headerBgTop = 8;
  const headerBgHeight = 100;
  doc.setFillColor(248, 250, 252);
  doc.rect(margin, headerBgTop, pageW - margin * 2, headerBgHeight, 'F');

  doc.setTextColor(15, 23, 42);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('CARGO MANIFEST', pageW / 2, y, { align: 'center' });
  y += 8;

  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'bold');
  doc.text(String(MANIFEST_COMPANY.name || '').toUpperCase(), margin, y);
  const agentNameLines = doc.splitTextToSize(String(agent.name || '—').toUpperCase(), rightTextWidth);
  agentNameLines.forEach((line, i) => {
    doc.text(line, rightColStart, y + i * 4);
  });
  y += Math.max(4, agentNameLines.length * 4);
  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'normal');
  const leftAddr = splitManifestAddressLines(MANIFEST_COMPANY.address);
  const rightAddr = splitManifestAddressLines(agent.address || '—');
  const addrLineCount = Math.max(leftAddr.length, rightAddr.length);
  for (let i = 0; i < addrLineCount; i += 1) {
    const rowY = y;
    doc.text(String(leftAddr[i] || '').toUpperCase(), margin, rowY);
    const rightChunks = doc.splitTextToSize(String(rightAddr[i] || '').toUpperCase(), rightTextWidth);
    const rLines = rightChunks.length ? rightChunks : [''];
    rLines.forEach((ln, j) => {
      doc.text(ln, rightColStart, rowY + j * 3.5);
    });
    y += Math.max(3.5, rLines.length * 3.5);
  }
  const yContactStart = y;
  const leftTextMaxW = Math.max(40, colMid - margin - 4);
  let leftLineCount = 0;
  let lcy = yContactStart;
  doc.setFont('helvetica', 'normal');
  if (MANIFEST_COMPANY.email) {
    const emLine = `${MANIFEST_LABEL.email} ${MANIFEST_COMPANY.email}`;
    const emLines = doc.splitTextToSize(emLine, leftTextMaxW);
    emLines.forEach((ln, i) => {
      doc.text(ln, margin, lcy + i * 3.5);
    });
    const n = emLines.length;
    lcy += n * 3.5;
    leftLineCount += n;
  }
  const telLineFull = `${MANIFEST_LABEL.tel} ${MANIFEST_COMPANY.tel}`.toUpperCase();
  const telLines = doc.splitTextToSize(telLineFull, leftTextMaxW);
  telLines.forEach((ln, i) => {
    doc.text(ln, margin, lcy + i * 3.5);
  });
  leftLineCount += telLines.length;

  let rightLineCount = 0;
  let rcy = yContactStart;
  doc.setFont('helvetica', 'normal');
  if (agent.emailLine || agent.telLine) {
    if (agent.emailLine) {
      const emLinesR = doc.splitTextToSize(agent.emailLine, rightTextWidth);
      emLinesR.forEach((ln, i) => {
        doc.text(ln, rightColStart, rcy + i * 3.5);
      });
      const n = emLinesR.length;
      rcy += n * 3.5;
      rightLineCount += n;
    }
    if (agent.telLine) {
      const telLinesR = doc.splitTextToSize(agent.telLine.toUpperCase(), rightTextWidth);
      telLinesR.forEach((ln, i) => {
        doc.text(ln, rightColStart, rcy + i * 3.5);
      });
      rcy += telLinesR.length * 3.5;
      rightLineCount += telLinesR.length;
    }
  } else if (agent.row3Right && agent.row3Right !== '—') {
    const parts = String(agent.row3Right).split('\n');
    for (let pi = 0; pi < parts.length; pi += 1) {
      const part = parts[pi];
      const { label, value } = parseManifestLabeledLine(part);
      const lineStr = label ? `${label}${value}` : value;
      const lines = doc.splitTextToSize(lineStr, rightTextWidth);
      const arr = lines.length ? lines : [''];
      for (let li = 0; li < arr.length; li += 1) {
        doc.text(arr[li], rightColStart, rcy + li * 3.5);
      }
      rcy += arr.length * 3.5;
      rightLineCount += arr.length;
    }
  }

  const maxContactLines = Math.max(leftLineCount, rightLineCount);
  const yAfterContact = yContactStart + Math.max(6.5, maxContactLines * 3.5);
  /** PDF-only space between company/contact block and B/L + totals block (no rule drawn). */
  const contactShipmentGapMm = 5.5;
  y = yAfterContact + contactShipmentGapMm;

  doc.setFontSize(8.5);
  doc.setTextColor(15, 23, 42);
  const leftMetaW = colMid - margin - 8;
  const rightMetaW = pageW - margin - rightColStart - 2;

  const nL1 = pdfDrawLabelBoldValue(
    doc,
    margin,
    y,
    'BILL OF LADING NO: ',
    String(meta.billOfLadingNo || '—').toUpperCase(),
    leftMetaW,
  );
  const nR1 = pdfDrawLabelBoldValue(
    doc,
    rightColStart,
    y,
    'TOTAL PACKAGES: ',
    String(meta.totalPackages).toUpperCase(),
    rightMetaW,
  );
  y += Math.max(4, Math.max(nL1, nR1) * 3.5);

  const nL2 = pdfDrawLabelBoldValue(
    doc,
    margin,
    y,
    'VESSEL: ',
    String(meta.vessel || '—').toUpperCase(),
    leftMetaW,
  );
  const nR2 = pdfDrawLabelBoldValue(
    doc,
    rightColStart,
    y,
    'TOTAL CBM: ',
    String(meta.totalCbm).toUpperCase(),
    rightMetaW,
  );
  y += Math.max(4, Math.max(nL2, nR2) * 3.5);

  const nL3 = pdfDrawLabelBoldValue(
    doc,
    margin,
    y,
    'CONTAINER NO: ',
    String(meta.container || '—').toUpperCase(),
    leftMetaW,
  );
  const nR3 = pdfDrawLabelBoldValue(
    doc,
    rightColStart,
    y,
    'TOTAL WEIGHT: ',
    `${meta.totalWeightKg} KG`.toUpperCase(),
    rightMetaW,
  );
  y += Math.max(4, Math.max(nL3, nR3) * 3.5);

  doc.setFont('helvetica', 'normal');
  y += 6;

  const body = rows.map((r) => [
    r.no,
    r.hbl,
    r.shipperDetails,
    r.consigneeDetails,
    String(r.pcs),
    (Math.round(r.cbm * 100) / 100).toFixed(2),
    String(Math.round(r.weight) || r.weight),
    r.description,
    r.upbGift,
  ]);

  const sub = shipmentCode || trackingNumber || 'Shipment';
  doc.setFont('helvetica', 'normal');

  const tableInnerW = pageW - margin * 2;
  const baseColW = [10, 14, 38, 48, 12, 14, 14, 52, 16];
  const baseSum = baseColW.reduce((a, b) => a + b, 0);
  const colScale = tableInnerW / baseSum;
  const scaledW = baseColW.map((w) => w * colScale);
  scaledW[scaledW.length - 1] += tableInnerW - scaledW.reduce((a, b) => a + b, 0);
  const columnStyles = {};
  for (let i = 0; i < scaledW.length; i += 1) {
    const h = i === 2 || i === 3 || i === 7 ? 'left' : 'center';
    columnStyles[i] = { cellWidth: scaledW[i], halign: h };
  }

  autoTable(doc, {
    head: [TABLE_HEADERS],
    body,
    startY: y,
    margin: { left: margin, right: margin, top: y },
    tableWidth: tableInnerW,
    showHead: 'everyPage',
    styles: {
      fontSize: 7,
      cellPadding: 1.5,
      overflow: 'linebreak',
      valign: 'top',
      lineWidth: 0.1,
      lineColor: [0, 0, 0],
    },
    headStyles: {
      fillColor: [30, 64, 175],
      textColor: 255,
      fontStyle: 'bold',
      halign: 'center',
    },
    columnStyles,
    didDrawPage: (data) => {
      doc.setFontSize(8);
      doc.setTextColor(100, 116, 139);
      const footer = `Cargo manifest — ${sub} · Page ${data.pageNumber}`;
      doc.text(footer, margin, doc.internal.pageSize.getHeight() - 6);
    },
  });

  const stem = manifestSafeFileStem(shipmentCode, trackingNumber);
  doc.save(`${stem}_cargo-manifest.pdf`);
}
